import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const SectionContainer = styled(Box)({
  padding: '120px 60px',
  background: '#0a0a0f',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.3), transparent)'
  },
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto'
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '64px',
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(99, 102, 241, 0.1)',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#818cf8',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: 'clamp(36px, 5vw, 56px)',
  fontWeight: 700,
  color: '#ffffff',
  lineHeight: 1.2,
  letterSpacing: '-1px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  animation: `${shimmer} 3s linear infinite`
});

const BentoGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridAutoRows: 'minmax(280px, auto)',
  gap: '24px',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr'
  }
});

const ServiceCard = styled(Box)(({ featured, index }) => ({
  position: 'relative',
  borderRadius: '24px',
  overflow: 'hidden',
  background: featured
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(168, 85, 247, 0.1) 100%)'
    : 'rgba(255, 255, 255, 0.03)',
  border: featured
    ? '1px solid rgba(99, 102, 241, 0.3)'
    : '1px solid rgba(255, 255, 255, 0.06)',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  cursor: 'pointer',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: `${index * 0.1}s`,
  animationFillMode: 'backwards',
  gridColumn: featured ? 'span 2' : 'span 1',
  '@media (max-width: 600px)': {
    gridColumn: 'span 1'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, transparent 50%)',
    opacity: 0,
    transition: 'opacity 0.5s ease'
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: 'rgba(99, 102, 241, 0.4)',
    boxShadow: '0 20px 60px rgba(99, 102, 241, 0.15)',
    '&::before': {
      opacity: 1
    },
    '& .service-icon': {
      transform: 'scale(1.1)',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    },
    '& .arrow-icon': {
      transform: 'translate(4px, -4px)',
      opacity: 1
    }
  }
}));

const CardTop = styled(Box)({
  position: 'relative',
  zIndex: 1
});

const ServiceIcon = styled(Box)({
  width: '64px',
  height: '64px',
  borderRadius: '18px',
  background: 'rgba(99, 102, 241, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
  transition: 'all 0.4s ease',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  '& svg': {
    fontSize: '28px',
    color: '#818cf8'
  }
});

const ServiceNumber = styled(Typography)({
  position: 'absolute',
  top: '0',
  right: '0',
  fontFamily: "'Outfit', sans-serif",
  fontSize: '72px',
  fontWeight: 800,
  color: 'rgba(255, 255, 255, 0.03)',
  lineHeight: 1,
  userSelect: 'none'
});

const ServiceTitle = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: '24px',
  fontWeight: 600,
  color: '#ffffff',
  marginBottom: '12px',
  position: 'relative',
  zIndex: 1
});

const ServiceDescription = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.7,
  position: 'relative',
  zIndex: 1
});

const CardBottom = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: '24px',
  position: 'relative',
  zIndex: 1
});

const PriceTag = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  color: '#818cf8'
});

const ArrowButton = styled(Box)({
  width: '44px',
  height: '44px',
  borderRadius: '12px',
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '20px',
    color: '#ffffff',
    opacity: 0.5,
    transition: 'all 0.3s ease'
  }
});

const ServicesSection = ({ services, products }) => {
  const allItems = [
    ...(services || []).map(s => ({ ...s, type: 'service' })),
    ...(products || []).map(p => ({ ...p, type: 'product' }))
  ].slice(0, 6);

  if (allItems.length === 0) return null;

  return (
    <SectionContainer id="services">
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <AutoAwesomeIcon sx={{ fontSize: 16, color: '#818cf8' }} />
            <BadgeText>What We Offer</BadgeText>
          </SectionBadge>
          <SectionTitle>
            Our <GradientText>Services</GradientText>
          </SectionTitle>
        </SectionHeader>

        <BentoGrid>
          {allItems.map((item, index) => (
            <ServiceCard
              key={item.id || index}
              index={index}
              featured={index === 0}
            >
              <CardTop>
                <ServiceNumber>{String(index + 1).padStart(2, '0')}</ServiceNumber>
                <ServiceIcon className="service-icon">
                  <AutoAwesomeIcon />
                </ServiceIcon>
                <ServiceTitle>{item.name || item.title}</ServiceTitle>
                <ServiceDescription>
                  {item.description || item.shortDescription || 'Premium service tailored to meet your unique needs.'}
                </ServiceDescription>
              </CardTop>
              <CardBottom>
                <PriceTag>
                  {item.price ? `$${item.price}` : 'Contact for pricing'}
                </PriceTag>
                <ArrowButton>
                  <ArrowOutwardIcon className="arrow-icon" />
                </ArrowButton>
              </CardBottom>
            </ServiceCard>
          ))}
        </BentoGrid>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default ServicesSection;
